import { toCalendarDay } from '@adornis/datetime/calendarDay.js';
import { confirmDialog, guardDialog } from '@adornis/dialog/dialog-functions.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { hoverTooltip } from '@adornis/popover/hover-tooltip.js';
import { computed } from '@lit-labs/preact-signals';
import { sub } from 'date-fns';
import { html, nothing } from 'lit';
import { BookingAPI } from '../../../../bookings/api/BookingAPI.js';
import { bookingActive, bookingFuture } from '../../../../bookings/api/bookingActive.js';
import { describeMandateRuntimeChanges } from '../../../../sepa/client/describeMandateRuntimeChanges.js';
import { Role } from '../../../../permissions/roles.js';
import { UserListSelectionInterface } from '../../../../users/list/entities/UserListSelectionInterface.js';
import type { GroupParticipantRow } from '../../api/GroupsDetailsAPI.js';

export const MemberInlineControls = stateful((props: { participantRow: GroupParticipantRow; frozen?: boolean }) => {
  const user = useObservable(() => currentUser(), []);
  // deleting takes every school year of the person with it, mandates included
  const canDelete = !!user?.roles.some(role => role.name === Role.ADMIN);
  const bookingIDs = props.participantRow.bookings.map(booking => booking._id);

  /**
   * Calculate the href for the booking detail page, to automatically select the user and the correct booking
   */
  const bookingHref = computed(() => {
    const dataInterface = new UserListSelectionInterface({
      userIDs: [props.participantRow.user._id],
    });

    const selectionString = JSON.stringify(dataInterface);
    return `/bookings?openBooking=${props.participantRow.booking._id}&selection=${selectionString}`;
  });

  return html`
    <div class="martinus-action-toolbar">
      <a
        data-tip="Zur Buchung"
        ${hoverTooltip()}
        class="btn btn-sm btn-ghost join-item text-base-content/70"
        href=${bookingHref}
      >
        <span class="icon-[material-symbols--contract-edit] text-lg"></span>
      </a>
      <a
        data-tip="Zur Person"
        ${hoverTooltip()}
        class="btn btn-sm btn-ghost join-item text-base-content/70"
        href="/personen/${props.participantRow.user._id}"
      >
        <span class="icon-[material-symbols--person-edit] text-lg"></span>
      </a>
      <button
        data-tip="Buchung beenden"
        ${hoverTooltip()}
        class="btn btn-sm btn-ghost btn-error join-item"
        @click=${async (e: Event) => {
          e.stopPropagation();
          e.preventDefault();

          const endDate = toCalendarDay(sub(new Date(), { days: 1 }));
          // the bookings `endForUserOnGroup` ends, with the end it gives them
          const ending = props.participantRow.bookings
            .filter(booking => bookingActive(booking, endDate) || bookingFuture(booking, endDate))
            .map(booking => ({ ...booking, endDate }));
          const mandateChanges = await describeMandateRuntimeChanges(ending);
          if (
            !(await confirmDialog(
              html`<div class="flex flex-col gap-3">
                <p>Die Buchung des Nutzers wirklich beenden?</p>
                ${mandateChanges ?? nothing}
              </div>`,
              {
                highlightDecline: true,
                acceptText: 'Buchung beenden',
                declineText: 'Abbrechen',
              },
            ))
          )
            return;

          await guardDialog(
            async () => {
              if (props.participantRow.group._id)
                return BookingAPI.endForUserOnGroup(
                  props.participantRow.group._id,
                  props.participantRow.user._id,
                  endDate,
                )();
              return 0;
            },
            {
              successText: 'Buchung wurde beendet',
            },
          );
        }}
      >
        <span class="icon-[material-symbols--calendar-clock] text-lg"></span>
      </button>
      ${
        // a group in billing lets bookings end, never be deleted
        props.frozen || !canDelete
          ? nothing
          : html`
              <button
                data-tip=${bookingIDs.length > 1 ? `${bookingIDs.length} Buchungen löschen` : 'Buchung löschen'}
                ${hoverTooltip()}
                class="btn btn-sm btn-error join-item"
                @click=${async (e: Event) => {
                  e.stopPropagation();
                  e.preventDefault();

                  if (
                    !(await confirmDialog(
                      bookingIDs.length > 1
                        ? `Alle ${bookingIDs.length} Buchungen der Person in dieser Gruppe samt ihrer SEPA-Mandate wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`
                        : 'Die Buchung des Nutzer samt ihrer SEPA-Mandate wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
                      {
                        highlightDecline: true,
                        acceptText: bookingIDs.length > 1 ? 'Buchungen löschen' : 'Buchung löschen',
                        declineText: 'Abbrechen',
                      },
                    ))
                  )
                    return;

                  await guardDialog(
                    async () => {
                      await BookingAPI.remove(bookingIDs)();
                    },
                    {
                      successText: bookingIDs.length > 1 ? 'Buchungen wurden gelöscht' : 'Buchung wurde gelöscht',
                    },
                  );
                }}
              >
                <span class="icon-[material-symbols--person-remove] text-lg"></span>
              </button>
            `
      }
    </div>
  `;
});
